import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { api } from '../api';
import ProfileCard from '../components/ProfileCard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const FIELDS = [
  { key: 'weight', label: 'Poids', unit: 'kg', color: '#10b981' },
  { key: 'waist', label: 'Tour de taille', unit: 'cm', color: '#f59e0b' },
  { key: 'chest', label: 'Poitrine', unit: 'cm', color: '#3b82f6' },
  { key: 'arm', label: 'Bras', unit: 'cm', color: '#a855f7' },
  { key: 'thigh', label: 'Cuisse', unit: 'cm', color: '#ef4444' },
];

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = () => ({ date: today(), weight: '', waist: '', chest: '', arm: '', thigh: '', notes: '' });

export default function Body() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [metric, setMetric] = useState('weight');
  const [error, setError] = useState(null);

  const { data: entries = [], isLoading } = useQuery({ queryKey: ['body'], queryFn: api.getBody });

  const save = useMutation({
    mutationFn: (payload) => (editingId ? api.updateBody(editingId, payload) : api.createBody(payload)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['body'] });
      setForm(emptyForm());
      setEditingId(null);
      setError(null);
    },
    onError: (e) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id) => api.deleteBody(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['body'] }),
  });

  const field = FIELDS.find((f) => f.key === metric);
  const series = useMemo(() => entries.filter((e) => e[metric] != null), [entries, metric]);
  const latest = series[series.length - 1];
  const first = series[0];
  const delta = latest && first && latest !== first ? latest[metric] - first[metric] : null;

  const submit = (e) => {
    e.preventDefault();
    const payload = { date: form.date, notes: form.notes || null };
    FIELDS.forEach((f) => { payload[f.key] = form[f.key] === '' ? null : parseFloat(form[f.key]); });
    save.mutate(payload);
  };

  const edit = (entry) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date,
      notes: entry.notes || '',
      ...Object.fromEntries(FIELDS.map((f) => [f.key, entry[f.key] ?? ''])),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const chartData = {
    labels: series.map((e) => new Date(`${e.date}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })),
    datasets: [{
      label: `${field.label} (${field.unit})`,
      data: series.map((e) => e[metric]),
      borderColor: field.color,
      backgroundColor: `${field.color}22`,
      fill: true,
      tension: 0.3,
    }],
  };

  return (
    <div>
      <h1 className="page-title">Suivi corporel</h1>
      <p className="page-subtitle">Poids et mensurations, pour voir l'évolution au-delà des charges</p>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>{editingId ? 'Modifier la mesure' : 'Nouvelle mesure'}</div>
        <form onSubmit={submit}>
          <div className="body-form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="body-date">Date</label>
              <input id="body-date" type="date" className="form-input" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            {FIELDS.map((f) => (
              <div className="form-group" key={f.key}>
                <label className="form-label" htmlFor={`body-${f.key}`}>{f.label} ({f.unit})</label>
                <input
                  id={`body-${f.key}`}
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  className="form-input"
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                />
              </div>
            ))}
          </div>
          {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" type="submit" disabled={save.isPending}>{editingId ? 'Mettre à jour' : 'Enregistrer'}</button>
            {editingId && (
              <button className="btn btn-ghost" type="button" onClick={() => { setEditingId(null); setForm(emptyForm()); }}>Annuler</button>
            )}
          </div>
        </form>
      </div>

      <ProfileCard />

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="chip-row" style={{ marginBottom: 16 }}>
          {FIELDS.map((f) => (
            <button key={f.key} className={`btn btn-sm ${metric === f.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMetric(f.key)}>{f.label}</button>
          ))}
        </div>
        {isLoading ? (
          <div className="loading"><div className="spinner" />Chargement...</div>
        ) : series.length === 0 ? (
          <div className="empty-state"><div className="icon">⚖️</div><p>Aucune mesure pour « {field.label} »</p></div>
        ) : (
          <>
            <div className="body-summary">
              <div><div className="stat-value">{latest[metric]} {field.unit}</div><div className="stat-label">Dernière mesure</div></div>
              {delta != null && (
                <div>
                  <div className="stat-value" style={{ color: 'var(--text)' }}>{delta > 0 ? '+' : ''}{Math.round(delta * 10) / 10} {field.unit}</div>
                  <div className="stat-label">Depuis le début</div>
                </div>
              )}
            </div>
            <div className="chart-container">
              <Line data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
            </div>
          </>
        )}
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: 16 }}>Historique</div>
        {entries.length === 0 ? (
          <div className="muted">Rien d'enregistré pour le moment.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...entries].reverse().map((e) => (
              <div key={e.id} className="session-item">
                <div>
                  <div className="session-day-name">{new Date(`${e.date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}</div>
                  <div className="session-date">
                    {FIELDS.filter((f) => e[f.key] != null).map((f) => `${f.label} ${e[f.key]} ${f.unit}`).join(' · ')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="icon-btn" onClick={() => edit(e)} aria-label="Modifier">✏️</button>
                  <button className="icon-btn" onClick={() => window.confirm('Supprimer cette mesure ?') && remove.mutate(e.id)} aria-label="Supprimer">🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
